/* ============================================================
   CBM Location pages — live opening-hours helpers
   - Renders an "Open now / Closed" pill from schema-driven hours
   - Highlights today's row in the opening-hours table
   Timezone aware (defaults to Australia/Sydney) so the status is
   correct for the store, not for the visitor's device.
   No dependencies. Safe to load more than once.
   ============================================================ */
(function () {
  'use strict';

  if (window.__cbmLocationHoursInit) return;
  window.__cbmLocationHoursInit = true;

  var DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  var DAY_LABELS = {
    sun: 'Sunday', mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday',
    thu: 'Thursday', fri: 'Friday', sat: 'Saturday'
  };

  /* Minutes since midnight from "HH:MM" — null if unparseable */
  function toMinutes(text) {
    if (!text) return null;
    var bits = String(text).trim().split(':');
    if (bits.length < 2) return null;
    var h = parseInt(bits[0], 10);
    var m = parseInt(bits[1], 10);
    if (isNaN(h) || isNaN(m)) return null;
    return h * 60 + m;
  }

  /* "10:00" -> "10am", "17:30" -> "5:30pm" */
  function friendly(text) {
    var mins = toMinutes(text);
    if (mins === null) return text || '';
    var h = Math.floor(mins / 60);
    var m = mins % 60;
    var ampm = h >= 12 ? 'pm' : 'am';
    var h12 = h % 12;
    if (h12 === 0) h12 = 12;
    return h12 + (m === 0 ? '' : ':' + (m < 10 ? '0' + m : m)) + ampm;
  }

  /* { open, close } in minutes for a day string like "10:00-17:30" */
  function parseRange(raw) {
    if (!raw) return null;
    var value = String(raw).trim().toLowerCase();
    if (!value || value === 'closed') return null;
    var parts = value.split('-');
    if (parts.length < 2) return null;
    var open = toMinutes(parts[0]);
    var close = toMinutes(parts[1]);
    if (open === null || close === null) return null;
    return { open: open, close: close, rawOpen: parts[0].trim(), rawClose: parts[1].trim() };
  }

  /* Current day index + minutes in the store's timezone */
  function nowInZone(tz) {
    var date = new Date();
    try {
      var fmt = new Intl.DateTimeFormat('en-AU', {
        timeZone: tz,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      var parts = fmt.formatToParts(date);
      var weekday = '', hour = null, minute = null;
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].type === 'weekday') weekday = parts[i].value.slice(0, 3).toLowerCase();
        if (parts[i].type === 'hour') hour = parseInt(parts[i].value, 10);
        if (parts[i].type === 'minute') minute = parseInt(parts[i].value, 10);
      }
      var index = DAYS.indexOf(weekday);
      if (index === -1 || hour === null || minute === null) throw new Error('unparsed');
      if (hour === 24) hour = 0; /* some engines emit 24 for midnight */
      return { index: index, minutes: hour * 60 + minute };
    } catch (e) {
      /* Fall back to the visitor's own clock rather than failing outright */
      return { index: date.getDay(), minutes: date.getHours() * 60 + date.getMinutes() };
    }
  }

  function readHours(el) {
    var raw = el.getAttribute('data-hours');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  /* ---- Open / closed pill ---- */
  function renderStatus(el) {
    var hours = readHours(el);
    if (!hours) return;
    var tz = el.getAttribute('data-tz') || 'Australia/Sydney';
    var now = nowInZone(tz);
    var todayKey = DAYS[now.index];
    var today = parseRange(hours[todayKey]);

    var labelEl = el.querySelector('[data-cbm-loc-status-label]');
    var detailEl = el.querySelector('[data-cbm-loc-status-detail]');
    if (!labelEl || !detailEl) return;

    el.classList.remove('is-open', 'is-closed');

    if (today && now.minutes >= today.open && now.minutes < today.close) {
      el.classList.add('is-open');
      labelEl.textContent = 'Open now';
      detailEl.textContent = 'Closes ' + friendly(today.rawClose);
      el.setAttribute('aria-label', 'Store is open now, closing at ' + friendly(today.rawClose));
      return;
    }

    /* Closed — work out the next opening */
    el.classList.add('is-closed');
    labelEl.textContent = 'Closed now';

    if (today && now.minutes < today.open) {
      detailEl.textContent = 'Opens today at ' + friendly(today.rawOpen);
      el.setAttribute('aria-label', 'Store is closed, opening today at ' + friendly(today.rawOpen));
      return;
    }

    for (var step = 1; step <= 7; step++) {
      var key = DAYS[(now.index + step) % 7];
      var range = parseRange(hours[key]);
      if (range) {
        var when = step === 1 ? 'tomorrow' : DAY_LABELS[key];
        detailEl.textContent = 'Opens ' + when + ' at ' + friendly(range.rawOpen);
        el.setAttribute('aria-label', 'Store is closed, opening ' + when + ' at ' + friendly(range.rawOpen));
        return;
      }
    }

    detailEl.textContent = 'See opening hours';
  }

  /* ---- Today's row in the hours table ---- */
  function highlightToday(table) {
    var tz = table.getAttribute('data-tz') || 'Australia/Sydney';
    var todayKey = DAYS[nowInZone(tz).index];
    var rows = table.querySelectorAll('tr[data-day]');
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].getAttribute('data-day') === todayKey) {
        rows[i].classList.add('is-today');
      } else {
        rows[i].classList.remove('is-today');
      }
    }
  }

  function init(root) {
    var scope = root || document;
    var pills = scope.querySelectorAll('[data-cbm-loc-status]');
    for (var i = 0; i < pills.length; i++) renderStatus(pills[i]);
    var tables = scope.querySelectorAll('[data-cbm-loc-hours-table]');
    for (var j = 0; j < tables.length; j++) highlightToday(tables[j]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Re-run inside the theme editor so the pill tracks setting changes */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  window.cbmLocationHours = { refresh: init };
})();
